import { Component, Vue } from "vue-property-decorator";
import { Radio, Modal, FormModel } from "ant-design-vue";
import { Icon, Button, Tag, Input, Checkbox, Select } from "ant-design-vue";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import {
	TagListResponseModel,
	TagListManagerService,
	CreateTagRequestModel,
	EditTagRequestModel
} from "@/services/text-guardian/tag-list";
import { ActionItem, fmtMsg, MessageHelper, NotificationType } from "commonui";
import { TagListLocale } from "@/locales/localeid";
import { sortTagList, insertSequence, getRouteAccessLevel, disabledDueToAccessLevel } from "@/util/func";
import PageTitle from "@/components/page-title/page-title.vue";

@Component({
	components: {
		Radio,
		RadioGroup: Radio.Group,
		Modal,
		Form: FormModel,
		FormItem: FormModel.Item,
		Icon,
		Button,
		Tag,
		Input,
		Checkbox,
		Select,
		SelectOption: Select.Option,
		Grid,
		TextArea: Input.TextArea,
		ActionItem,
		PageTitle
	}
})
export default class TagList extends Vue {
	public addNewTagModalVisible = false;
	public editTagModalVisible = false;
	public saveButtonLoading = false;
	public enableSaveButton = false;
	public disableCancelButton = false;
	public loading: boolean = false;
	public tagListService = TagListManagerService();
	public format = fmtMsg;
	public locale = TagListLocale;
	public hasUserEdited = false;
	public AddOrEditTagForm = {
		id: -1,
		code: "",
		description: ""
	};
	public AddTagFormRules = {
		code: [
			{ required: true, message: fmtMsg(TagListLocale.TagListFormRulesCodeRequired), trigger: "change" },
			{ validator: this.codeValidator, trigger: "change" }
		]
	};

	public EditTagFormRules = {
		code: [{ validator: this.codeValidator, trigger: "change" }],
		description: [{ validator: this.descriptionValidator, trigger: "change" }]
	};

	public labelCol = {
		span: 8
	};

	public wrapperCol = {
		span: 16
	};

	public tagList: TagListResponseModel[] = [];

	public validateAddOrEditTagForm(item: string, isPassed: boolean, error: string) {
		if (isPassed != this.enableSaveButton) this.enableSaveButton = isPassed;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	showAddNewTagModal() {
		this.addNewTagModalVisible = true;
	}

	onTagModalClose() {
		this.addNewTagModalVisible = false;
		this.editTagModalVisible = false;
		this.saveButtonLoading = false;
		this.hasUserEdited = false;
		this.enableSaveButton = false;
		this.disableCancelButton = false;

		// explicitly setting the values of form fields since after edit modal is closed
		// form.resetFields() method does not reset it to empty fields.
		this.resetFormFields();
	}

	resetFormFields() {
		this.AddOrEditTagForm.id = 0;
		this.AddOrEditTagForm.code = "";
		this.AddOrEditTagForm.description = "";
	}

	onTagModalOk(e: Event) {
		e.preventDefault();
		const newForm = this.$refs.tagForm as FormModel;
		newForm.validate((valid) => {
			if (valid) {
				this.saveButtonLoading = true;
				this.disableCancelButton = true;
				if (this.addNewTagModalVisible) {
					const addTagModel: any = { ...this.AddOrEditTagForm };
					this.tagListService.createTag(addTagModel as CreateTagRequestModel).then((result) => {
						if (result) {
							this.fetchTagList()
								.then(() => {
									this.onTagModalClose();
									MessageHelper.Message(
										NotificationType.Success,
										fmtMsg(TagListLocale.TagListAddTagSuccessMessage)
									);
								})
								.catch((err) => {
									this.saveButtonLoading = false;
									this.disableCancelButton = false;
									MessageHelper.Message(
										NotificationType.Failed,
										fmtMsg(TagListLocale.TagListAddTagFailureMessage)
									);
								});
						} else {
							this.onTagModalClose();
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(TagListLocale.TagListAddTagFailureMessage)
							);
						}
					});
				} else if (this.editTagModalVisible) {
					const editTagModel: any = { ...this.AddOrEditTagForm };
					this.tagListService.editTag(editTagModel as EditTagRequestModel).then((result) => {
						if (result) {
							this.fetchTagList()
								.then(() => {
									this.onTagModalClose();
									MessageHelper.Message(
										NotificationType.Success,
										fmtMsg(TagListLocale.TagListEditTagSuccessMessage)
									);
								})
								.catch((err) => {
									this.saveButtonLoading = false;
									MessageHelper.Message(
										NotificationType.Failed,
										fmtMsg(TagListLocale.TagListEditTagFailureMessage)
									);
								});
						} else {
							this.onTagModalClose();
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(TagListLocale.TagListEditTagFailureMessage)
							);
						}
					});
				}
			}
		});
	}

	fetchTagList() {
		this.loading = true;
		return this.tagListService
			.getTagList()
			.then((tags) => {
				const tagList = tags.map((tag) => tag);
				let newTagList = sortTagList(tagList, "code");
				newTagList = insertSequence(newTagList);
				this.tagList = newTagList;
				this.loading = false;
				return Promise.resolve();
			})
			.catch(() => {
				this.loading = false;
			});
	}

	showEditTagModal() {
		this.editTagModalVisible = true;
	}

	onCodeClick(tagId) {
		//show the tag modal with fields populated in the form.
		const tag = this.tagList.find((tag) => tag.id == tagId);
		this.AddOrEditTagForm.id = tag?.id as number;
		this.AddOrEditTagForm.code = tag?.code as string;
		this.AddOrEditTagForm.description = tag?.description as string;

		this.showEditTagModal();
	}
	get getModalTitle() {
		if (this.addNewTagModalVisible) {
			return fmtMsg(TagListLocale.TagListModalTitleAddnewtag);
		} else if (this.editTagModalVisible) {
			return fmtMsg(TagListLocale.TagListModalTitleEdittag);
		}
	}
	removeTag(tagId, event) {
		const tag = this.tagList.find((tag) => tag.id === tagId);
		if ((tag?.contentTextCount as number) > 0) {
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(TagListLocale.TagListDeleteTagWarningMessage, { contentTextCount: tag?.contentTextCount })
			);
		} else {
			Modal.confirm({
				title: fmtMsg(TagListLocale.TagListConfirmModalTitleText),
				okText: fmtMsg(TagListLocale.TagListConfirmModalOkText),
				okType: "danger",
				cancelText: fmtMsg(TagListLocale.TagListConfirmModalCancelText),
				onOk: () => {
					this.tagListService
						.deleteTag(tagId)
						.then((result) => {
							if (result) {
								MessageHelper.Message(
									NotificationType.Success,
									fmtMsg(TagListLocale.TagListDeleteTagSuccessMessage)
								);
								this.tagList = this.tagList.filter((tag) => tag.id !== tagId);
							} else if (!result) {
								MessageHelper.Message(
									NotificationType.Failed,
									fmtMsg(TagListLocale.TagListDeleteTagFailureMessage)
								);
							}
						})
						.catch((err) => {
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(TagListLocale.TagListDeleteTagFailureMessage)
							);
						});
				}
			});
		}
	}

	created() {
		this.fetchTagList();
	}

	validateCodeFormat(code, callback) {
		const regex = RegExp("^\\d+(\\.\\d+)*( *)(\\({1}[a-zA-Z0-9]+\\){1})*$", "gm");
		if (!regex.test(code)) {
			callback(fmtMsg(TagListLocale.TagListFormRulesCodeFormat));
			return;
		}
	}

	codeValidator(rule: any, value: string, callback: (msg?: string) => void) {
		let codeExists = false;

		this.validateCodeFormat(value, callback);

		if (this.editTagModalVisible && this.AddOrEditTagForm.id != -1) {
			this.hasUserEdited = true;
			codeExists = this.tagList.some(
				(tag) =>
					tag.id != this.AddOrEditTagForm.id &&
					tag.code.trim().toLocaleLowerCase() === value.trim().toLocaleLowerCase()
			);
		} else {
			codeExists = this.tagList.some(
				(tag) => tag.code.trim().toLocaleLowerCase() === value.trim().toLocaleLowerCase()
			);
		}
		if (codeExists) {
			callback(fmtMsg(TagListLocale.TagListFormRulesCodeUnique));
			return;
		}
		callback();
	}

	descriptionValidator(rule: any, value: string, callback: (msg?: string) => void) {
		if (this.editTagModalVisible && this.AddOrEditTagForm.id != -1) {
			this.hasUserEdited = true;
		}
		callback();
	}
}
